import React, { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule, ICellRendererParams } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { Carte } from '../../../utils/types/Carte';

ModuleRegistry.registerModules([AllCommunityModule]);

const InputCellRenderer = (props: ICellRendererParams<Carte>) => {
    const { value, data, colDef } = props;
    const handleCarteUpdated = colDef?.cellRendererParams?.handleCarteUpdated;
    const field = colDef?.field;

    let isEditable = false;
    if (field === 'allocationQte') {
        isEditable = data?.idArticle !== null;
    } else if (field === 'allocationValeur') {
        isEditable = data?.idArticle === null;
    }

    if (isEditable) {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;

            if (inputValue === '') {
                const updatedCarte = { ...data, [field ?? '']: null };

                if (handleCarteUpdated) {
                    handleCarteUpdated(updatedCarte);
                }

                return;
            }

            // Remplacer la virgule par un point pour que parseFloat fonctionne
            const normalizedValue = inputValue.replace(',', '.');
            const parsedValue = parseFloat(normalizedValue);
            if (!isNaN(parsedValue)) {
                const updatedCarte = { ...data, [field ?? '']: parsedValue };

                if (handleCarteUpdated) {
                    handleCarteUpdated(updatedCarte);
                }
            }
        };

        return <input type="number" value={value ?? ''} min={0} onChange={handleChange} style={{ width: '100%', height: '100%' }} />;
    } else {
        return <span>{value ?? ''}</span>;
    }
};

interface CarteGridProps {
    cartes: Carte[];
    handleCarteUpdated?: (carte: Carte) => void;
}

const MultiAllocationCarteGrid: React.FC<CarteGridProps> = ({ cartes, handleCarteUpdated }) => {
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs: ColDef<Carte>[] = [
        { field: 'numero', headerName: 'Numéro', sortable: true, filter: true, pinned: 'left' },
        { field: 'type', headerName: 'Type', sortable: true, filter: true },
        { field: 'description', headerName: 'Porteur', sortable: true, filter: true },
        { field: 'designationArticle', headerName: 'Article', sortable: true, filter: true },
        {
            headerName: 'Recharge Quantité (L)',
            field: 'allocationQte',
            cellRenderer: InputCellRenderer,
            cellRendererParams: {
                handleCarteUpdated: handleCarteUpdated,
            },
            cellStyle: { textAlign: 'center' },
            wrapText: true,
            autoHeight: true,
        },
        {
            headerName: 'Recharge Valeur ($)',
            field: 'allocationValeur',
            cellRenderer: InputCellRenderer,
            cellRendererParams: {
                handleCarteUpdated: handleCarteUpdated,
            },
            cellStyle: { textAlign: 'center' },
            wrapText: true,
            autoHeight: true,
        },
    ];

    // ---

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const onCellValueChanged = (params: any) => {
        const updatedCarte = { ...params.data };
        if (handleCarteUpdated) {
            handleCarteUpdated(updatedCarte);
        }
    };

    // ---

    return (
        <div className="" style={{ height: 400, width: '100%' }}>
            <AgGridReact
                ref={gridRef}
                theme={myTheme}
                getRowId={(params) => params.data.id.toString()}
                rowData={cartes}
                columnDefs={columnDefs}
                pagination={true}
                domLayout="normal"
                defaultColDef={{
                    resizable: true,
                    minWidth: 100,
                    flex: 1,
                }}
                suppressClickEdit={true}
                suppressExcelExport={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                animateRows={true}
                onCellValueChanged={onCellValueChanged}
                overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
            />
        </div>
    );
};

export default MultiAllocationCarteGrid;
